import VimeoPlayer from "@vimeo/player";
import YouTubePlayerFactory from "youtube-player";
import { YouTubePlayer } from "youtube-player/dist/types";

const youTubeRegex = new RegExp(
  "^(https?://)?(www.)?(youtube(-nocookie)?.com|youtu.be)/.+$"
);

const vimeoRegex = new RegExp("^(https?://)?(www.)?(vimeo.com|player.vimeo.com)/.+$");

export const isYouTubeUrl = (url: string): boolean => !!url.match(youTubeRegex);

const isVimeoVideo = (url: string) => url.match(vimeoRegex) ?? false;

export const getPlayer = (iframe: HTMLIFrameElement) => {
  if (isYouTubeUrl(iframe.src)) {
    return YouTubePlayerFactory(iframe);
  }

  if (isVimeoVideo(iframe.src)) {
    return new VimeoPlayer(iframe);
  }

  console.warn("Autoplay is only supported on YouTube and Vimeo videos");
};

export const isYouTubePlayer = (
  player: YouTubePlayer | VimeoPlayer
): player is YouTubePlayer => (player as YouTubePlayer).playVideo !== undefined;

export const isVimeoPlayer = (
  player: YouTubePlayer | VimeoPlayer
): player is VimeoPlayer => (player as VimeoPlayer).play !== undefined;

export const getVideoDuration = async (player: YouTubePlayer | VimeoPlayer) => {
  const totalSeconds = await player.getDuration();

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { hours, minutes, seconds };
};

const getVimeoVideoId = (url: string) => {
  const match = url.match(/vimeo\.com\/(\d+)/);

  return match ? match[1] : null;
};

export const setVideoDurationStructuredData = async (
  player: YouTubePlayer | VimeoPlayer
) => {
  const { hours, minutes, seconds } = await getVideoDuration(player);

  const scriptElements = document.querySelectorAll('script[type="application/ld+json"]');

  for (const script of scriptElements) {
    if (script.textContent) {
      const json = JSON.parse(script.textContent);

      if (json.embedUrl) {
        let match = false;

        // The url returned from player.getVideoUrl() is generally shorter than the one in the
        // cms for Vimeo videos, so compare by id instead.
        if (isVimeoPlayer(player)) {
          const id1 = (await player.getVideoId()).toString();
          const id2 = getVimeoVideoId(json.embedUrl);

          if (id1 && id2 && id1 === id2) {
            match = true;
          }
        } else {
          const videoUrl = await player.getVideoUrl();

          match = videoUrl === json.embedUrl;
        }

        if (match) {
          json.duration = `PT${hours}H${minutes}M${seconds}S`;
          script.textContent = JSON.stringify(json);
          break;
        }
      }
    }
  }
};
